import { FC, ReactNode } from 'react';
import { Box, Flex, Palette, Text } from '@updater/ui-uds';
import { TvInternetOffer } from '@updater/consumer-graph';
import {
  useOfferContext,
  getDataCapCopy,
  getProviderContractText,
} from '@updater/ui-widgets';
import { CalendarBlank, Database, File, WifiHigh } from '@phosphor-icons/react';
import styled from '@emotion/styled';
import { getEquipmentRentalText } from 'utils/get-equipment-rental-text';

const InfoRow = styled(Flex)`
  padding: 12px 0;
  border-bottom: 1px solid ${Palette.gray300};
  color: ${Palette.blue950};

  &:last-child {
    border-bottom: none;
  }
`;

/**
 * Extracts a short data cap label from the full copy.
 * Falls back to the full text if no short version can be derived.
 */
function getShortDataCap(fullCopy: string | null): string | null {
  if (!fullCopy) return null;
  const lower = fullCopy.toLowerCase();
  if (lower.includes('unlimited') || lower.includes('no data cap'))
    return 'Unlimited';
  const tbMatch = fullCopy.match(/([\d.]+)\s*(?:terabyte|tb)/i);
  if (tbMatch) return `${tbMatch[1]} TB`;
  const gbMatch = fullCopy.match(/(\d+)\s*gb/i);
  if (gbMatch) return `${gbMatch[1]} GB`;
  return fullCopy;
}

export const PlanInformationSection: FC = () => {
  const { products, contract, providerCode, name, options } =
    useOfferContext() as TvInternetOffer;
  const downloadSpeed = products?.internet?.downloadSpeed?.value ?? 0;

  const fullDataCopy = getDataCapCopy({
    providerCode: providerCode ?? '',
    downloadSpeed,
  });
  const shortDataCap = getShortDataCap(
    typeof fullDataCopy === 'string' ? fullDataCopy : null
  );

  const contractText = getProviderContractText({
    contractLength: contract?.term?.length,
    providerCode,
    products,
  });

  const installationAmount = options?.installationOptionsList?.[0]?.amount;

  const equipmentText = getEquipmentRentalText(
    name ?? '',
    providerCode ?? '',
    Boolean(products?.tv)
  );

  const items: {
    icon: ReactNode;
    label: string;
    value: string;
    valueColor?: string;
  }[] = [];

  if (shortDataCap) {
    items.push({
      icon: <Database size={18} color={Palette.blue950} />,
      label: 'Data Cap',
      value: shortDataCap,
    });
  }

  items.push({
    icon: <File size={18} color={Palette.blue950} />,
    label: 'Contract',
    value: contractText,
  });

  items.push({
    icon: <CalendarBlank size={18} color={Palette.blue950} />,
    label: 'Installation',
    value:
      installationAmount != null ? `$${Math.round(installationAmount)}` : '$0',
    valueColor: Palette.mint600,
  });

  if (equipmentText) {
    items.push({
      icon: <WifiHigh size={18} color={Palette.blue950} />,
      label: 'Equipment',
      value: equipmentText,
    });
  }

  return (
    <Box mb="m">
      <Text variant="mBold" mb="s">
        Plan information
      </Text>
      {items.map((item) => (
        <InfoRow
          key={item.label}
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex alignItems="center">
            <Box mr="xs">{item.icon}</Box>
            <Text variant="s">{item.label}</Text>
          </Flex>
          <Text variant="sBold" color={item.valueColor}>
            {item.value}
          </Text>
        </InfoRow>
      ))}
    </Box>
  );
};
